import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css_pages/table.css";
import "../css_pages/form.css";

const API_URL = "https://api.allianceprotravaux.be/api/categories";

const EMPTY_FORM = { name: "", description: "", isActive: true, image: null };

/* Normalize a category from the API response into a consistent shape.
   - Extracts image URL whether the API returns a string or an object { url }
   - Falls back to the existing image if the API response omits it (e.g. after edit)
   - Maps is_active (snake_case) to isActive (camelCase) */
const normalizeCategory = (cat, fallbackImage = null) => {
  const image = typeof cat.image === "object" ? cat.image?.url : cat.image;
  return {
    ...cat,
    image: image || fallbackImage,
    isActive: cat.is_active !== false && cat.is_active !== 0,
  };
};

export default function Categori({ search }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  /* Fetch all categories on mount */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(API_URL);
        setCategories(res.data.map((c) => normalizeCategory(c)));
      } catch (err) {
        console.error("Failed to load categories:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* Show a success message for 3 seconds then clear it */
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /* Open the drawer for adding (no argument) or editing (pass the category) */
  const openDrawer = (cat = null) => {
    if (cat) {
      setFormData({
        name: cat.name,
        description: cat.description || "",
        isActive: cat.isActive,
        image: null,
      });
      setEditingId(cat.id);
    } else {
      setFormData(EMPTY_FORM);
      setEditingId(null);
    }
    setShowDrawer(true);
  };

  /* Close the drawer and reset form state */
  const closeDrawer = () => {
    setShowDrawer(false);
    setFormData(EMPTY_FORM);
    setEditingId(null);
  };

  /* Submit the form — creates a new category or updates an existing one */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = new FormData();
    payload.append("name", formData.name);
    payload.append("description", formData.description);
    payload.append("is_active", formData.isActive ? 1 : 0);

    if (formData.image instanceof File) {
      // New image selected — send the file
      payload.append("image", formData.image);
    } else if (editingId) {
      // No new image selected — preserve the existing image URL
      const existingImage = categories.find((c) => c.id === editingId)?.image;
      if (existingImage) payload.append("image_url", existingImage);
    }

    try {
      if (editingId) {
        // Laravel requires _method spoofing for PUT with multipart
        payload.append("_method", "PUT");
        const res = await axios.post(`${API_URL}/${editingId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const existingImage = categories.find((c) => c.id === editingId)?.image;
        setCategories(categories.map((c) =>
          c.id === editingId ? normalizeCategory(res.data, existingImage) : c
        ));
        showSuccess("Catégorie modifiée avec succès !");
      } else {
        const res = await axios.post(API_URL, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setCategories([...categories, normalizeCategory(res.data)]);
        showSuccess("Catégorie ajoutée avec succès !");
      }
      closeDrawer();
    } catch (err) {
      console.error("Failed to save category:", err);
      alert("Erreur lors de l'enregistrement de la catégorie.");
    }
  };

  /* Toggle a category's active/inactive status */
  const toggleStatus = async (id) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    try {
      const res = await axios.put(`${API_URL}/${id}`, {
        ...cat,
        is_active: !cat.isActive,
      });
      setCategories(categories.map((c) =>
        c.id === id ? normalizeCategory(res.data, cat.image) : c
      ));
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  /* Delete a category after confirmation */
  const deleteCategory = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`);
      setCategories(categories.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Failed to delete category:", err);
      alert("Erreur lors de la suppression.");
    }
  };

  /* Filter categories by name or description based on the search prop */
  const filtered = categories.filter((cat) => {
    const q = (search || "").toLowerCase();
    return (
      cat.name.toLowerCase().includes(q) ||
      (cat.description || "").toLowerCase().includes(q)
    );
  });

  // The existing image to preview when editing (before picking a new file)
  const editingImage = editingId ? categories.find((c) => c.id === editingId)?.image : null;
  const previewSrc = formData.image ? URL.createObjectURL(formData.image) : editingImage;

  return (
    <div className="categories-page" style={{ position: "relative", minHeight: "80vh" }}>

      {/* Success notification */}
      {successMessage && (
        <div style={{ padding: "10px 20px", background: "#4caf50", color: "white", marginBottom: "1rem", borderRadius: "6px" }}>
          {successMessage}
        </div>
      )}

      {/* Page header */}
      <div className="page-header" style={{ marginBottom: "2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Gestion des Catégories</h2>
        <button className="btn-primary" onClick={() => openDrawer()} style={{ padding: "10px 20px" }}>
          + Ajouter une catégorie
        </button>
      </div>

      {/* Categories table */}
      {loading ? (
        <p>Chargement des catégories...</p>
      ) : (
        <div className="table-wrapper">
          <div className="table-header">Liste des Catégories</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Nom</th>
                <th>Description</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((cat) => (
                <tr key={cat.id}>
                  <td>
                    {cat.image
                      ? <img src={cat.image} alt={cat.name} width={50} height={50} />
                      : "-"}
                  </td>
                  <td style={{ fontWeight: "bold", width: "200px" }}>{cat.name}</td>
                  <td style={{ maxWidth: "300px", whiteSpace: "normal", wordWrap: "break-word", lineHeight: "1.4" }}>
                    {cat.description || "-"}
                  </td>
                  <td>
                    <span
                      className={`badge ${cat.isActive ? "active" : "inactive"}`}
                      onClick={() => toggleStatus(cat.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {cat.isActive ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="edit-btn" onClick={() => openDrawer(cat)}>Modifier</button>
                      <button className="delete-btn" onClick={() => deleteCategory(cat.id)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>Aucune catégorie trouvée</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer — slide-in panel for add/edit form */}
      {showDrawer && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1000, backdropFilter: "blur(2px)" }}
            onClick={closeDrawer}
          />

          {/* Panel */}
          <div style={{
            position: "fixed", top: 0, right: 0,
            width: "100%", maxWidth: "400px", height: "100vh",
            background: "white", zIndex: 1001,
            boxShadow: "-4px 0 15px rgba(0,0,0,0.1)",
            padding: "30px", display: "flex", flexDirection: "column",
          }}>
            {/* Drawer header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
              <h3 style={{ margin: 0 }}>{editingId ? "Modifier Catégorie" : "Ajouter Catégorie"}</h3>
              <button onClick={closeDrawer} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666" }}>
                ×
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Nom de la catégorie</label>
                <input
                  type="text"
                  placeholder="Ex: Électricité"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Description</label>
                <textarea
                  placeholder="Description courte..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ width: "100%", height: "120px", padding: "12px", borderRadius: "8px", border: "1px solid #ddd", resize: "none" }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                />
                {/* Image preview — shows new file if selected, otherwise existing image */}
                {previewSrc && (
                  <img
                    src={previewSrc}
                    alt="Preview"
                    width={100}
                    height={100}
                    style={{ marginTop: "10px", borderRadius: "6px", objectFit: "cover" }}
                  />
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                />
                <label htmlFor="isActive" style={{ marginBottom: 0, cursor: "pointer" }}>Activer la catégorie</label>
              </div>

              <div style={{ marginTop: "auto", display: "flex", gap: "10px" }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  {editingId ? "Enregistrer" : "Ajouter"}
                </button>
                <button type="button" className="btn-secondary" onClick={closeDrawer} style={{ flex: 1 }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}